/**
 * @fileoverview Auth modal context for programmatically controlling the auth modal
 * @module features/auth/contexts/AuthModalContext
 */

import type { ReactNode, ReactElement } from 'react';
import { createContext, useContext, useState, useCallback } from 'react';

/**
 * Auth modal context interface.
 */
interface AuthModalContextValue {
  /** Whether the auth modal is open */
  isOpen: boolean;
  /** Open the auth modal */
  openModal: () => void;
  /** Close the auth modal */
  closeModal: () => void;
}

/**
 * Auth modal context.
 */
const AuthModalContext = createContext<AuthModalContextValue | null>(null);

/**
 * Props for AuthModalProvider.
 */
interface AuthModalProviderProps {
  children: ReactNode;
}

/**
 * Provider for auth modal context.
 *
 * Allows any component to programmatically open/close the auth modal.
 *
 * @component
 * @example
 * ```tsx
 * // In your app root
 * <AuthModalProvider>
 *   <App />
 * </AuthModalProvider>
 *
 * // In any component
 * const { openModal } = useAuthModal();
 * <button onClick={openModal}>Sign In</button>
 * ```
 */
export function AuthModalProvider({ children }: AuthModalProviderProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false);

  const openModal = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  const value: AuthModalContextValue = {
    isOpen,
    openModal,
    closeModal,
  };

  return (
    <AuthModalContext.Provider value={value}>
      {children}
    </AuthModalContext.Provider>
  );
}

/**
 * Hook to access auth modal context.
 *
 * @throws {Error} If used outside AuthModalProvider
 * @returns Auth modal context value
 *
 * @example
 * ```tsx
 * const { openModal, closeModal, isOpen } = useAuthModal();
 * ```
 */
export function useAuthModal(): AuthModalContextValue {
  const context = useContext(AuthModalContext);

  if (!context) {
    throw new Error('useAuthModal must be used within AuthModalProvider');
  }

  return context;
}
